import * as React from "react";
import { DropDown } from "./DropDown";
import { DropDownItem } from "./DropDown/DropDown";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import {
  $getSelection,
  $isRangeSelection,
  LexicalEditor,
  $isRootOrShadowRoot,
} from "lexical";
import {
  Greek,
  IconNumber,
  ListOl,
  LowerAlpha,
  LowerRoman,
  UpperAlpha,
  UpperRoman,
} from "../../../assets/icons/SvgIcons";
import { numberTypeList } from "../Helper";
import { $findMatchingParent, $getNearestNodeOfType } from "@lexical/utils";
import { ListTypeProps, toolTipConstants } from "../LexicalEditor.types";

export function NumberList({
  editor,
  numberType,
  disabled = false,
  setNumberType,
  listType,
  setListType,
}: {
  numberType: keyof typeof numberTypeList;
  editor: LexicalEditor;
  disabled?: boolean;
  setNumberType: (type: string) => void;
  listType: ListTypeProps;
  setListType: (type: ListTypeProps) => void;
}): JSX.Element {
  const formatNumberList = (type: keyof typeof numberTypeList) => {
    setNumberType(type); // Always set the key, not the value
    if (listType !== "number") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
      setListType("number");
    }
    // Always update the style after command
    setTimeout(() => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const element = anchorNode.getTopLevelElementOrThrow();
          const elementDOM = editor.getElementByKey(element.getKey());
          if (elementDOM && elementDOM.tagName.toLowerCase() === "ol") {
            switch (type) {
              case "capitalRoman":
                elementDOM.style.listStyleType = "upper-roman";
                break;
              case "smallRoman":
                elementDOM.style.listStyleType = "lower-roman";
                break;
              case "capitalAlpha":
                elementDOM.style.listStyleType = "upper-alpha";
                break;
              case "smallAlpha":
                elementDOM.style.listStyleType = "lower-alpha";
                break;
              case "lowerGreek":
                elementDOM.style.listStyleType = "lower-greek";
                break;
              default:
                elementDOM.style.listStyleType = "decimal";
            }
          }
        }
      });
    }, 0);
  };

  React.useEffect(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        console.log("slection", selection);
        const anchorNode = selection.anchor.getNode();
        let element =
          anchorNode.getKey() === "root"
            ? anchorNode
            : $findMatchingParent(anchorNode, (e) => {
                const parent = e.getParent();
                return parent !== null && $isRootOrShadowRoot(parent);
              });
        if (element === null) {
          element = anchorNode.getTopLevelElementOrThrow();
        }
        const elementKey = element.getKey();
        const elementDOM = editor.getElementByKey(elementKey);
        if (elementDOM !== null) {
          if ($isListNode(element)) {
            const parentList = $getNearestNodeOfType<ListNode>(
              anchorNode,
              ListNode
            );
            const type = parentList
              ? parentList.getListType()
              : element.getListType();
            setListType(type);
            switch (numberType) {
              case "smallAlpha":
                elementDOM.style.listStyleType = "lower-alpha";
                console.log("upper alpha", elementDOM);
                break;
              case "capitalAlpha":
                elementDOM.style.listStyleType = "upper-alpha";
                console.log("upper alpha", elementDOM);
                break;
              case "smallRoman":
                elementDOM.style.listStyleType = "lower-roman";
                console.log("upper alpha", elementDOM);
                break;
              case "capitalRoman":
                elementDOM.style.listStyleType = "upper-roman";
                console.log("upper alpha", elementDOM);
                break;
              case "lowerGreek":
                elementDOM.style.listStyleType = "lower-greek";
                console.log("upper alpha", elementDOM);
                break;
              default:
                elementDOM.style.listStyleType = "number";
            }
          }
        }
      }
    });
  }, [numberType, listType]);

  const handleButtonClick = () => {
    if (listType === "number") {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      setListType(null);
    } else {
      setNumberType(numberTypeList["number"]);
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  };

  const numberTypeIcons = {
    number: ListOl,
    smallAlpha: LowerAlpha,
    capitalAlpha: UpperAlpha,
    smallRoman: LowerRoman,
    capitalRoman: UpperRoman,
    lowerGreek: Greek,
  };

  const getNumberListTitle = (type: keyof typeof numberTypeList) => {
    switch (type) {
      case "number":
        return toolTipConstants.number
      case "smallAlpha":
        return toolTipConstants.lowerAlpha;
      case "capitalAlpha":
        return toolTipConstants.upperAlpha;
      case "smallRoman":
        return toolTipConstants.lowerRoman;
      case "capitalRoman":
        return toolTipConstants.upperRoman;
      case "lowerGreek":
        return toolTipConstants.lowerGreek;
      default:
        return ""
    };
  };

  return (
    <DropDown
      disabled={disabled}
      buttonClassName="lexical-toolbarItem"
      buttonAriaLabel="Formatting options for list style"
      onClickContent={handleButtonClick}
      active={listType === "number"}
      dropdownItemClass="lexical-dropdownList"
      ButtonIcon={IconNumber}
      iconClassName="lexical-iconList2"
      title={toolTipConstants.numberList}
    >
      {Object.entries(numberTypeList).map(([key, value]) => {
        const IconComponent =
          numberTypeIcons[key as keyof typeof numberTypeIcons];

        return (
          <DropDownItem
            key={key}
            className={`lexical-toolbarItem ${
              numberType === key && listType === "number"
                ? "lexical-itemActive"
                : ""
            }`}
            title={getNumberListTitle(key as keyof typeof numberTypeList)}
            onClick={() => formatNumberList(key as keyof typeof numberTypeList)}
          >
            <IconComponent />
          </DropDownItem>
        );
      })}
    </DropDown>
  );
}
